// Helpers for language-prefixed SEO URLs.
// German (de) is the default and lives at the root (no prefix); every other
// language gets a URL prefix, e.g. /en/blog/... , /fr/transfer/... , /ar/ .
import { useLocation } from "react-router-dom";
import { LANGUAGES } from "./translations";
import { aliasToCity } from "../data/cities";

export const LANG_CODES = LANGUAGES.map((l) => l.code);
export const DEFAULT_LANG = "de";

// Per-language SEO keyword prefix for city landing pages (ASCII, hyphen-terminated).
// e.g. /flughafentransfer-zuerich (de), /en/airport-transfer-zuerich (en).
export const CITY_KW = {
  de: "flughafentransfer-",
  en: "airport-transfer-",
  fr: "transfert-aeroport-",
  it: "trasferimento-aeroporto-",
  es: "traslado-aeropuerto-",
  pt: "transfer-aeroporto-",
  tr: "havalimani-transfer-",
  ru: "aeroport-transfer-",
  ar: "naql-almatar-",
  fa: "transfer-forudgah-",
  hi: "airport-transfer-",
  zh: "airport-transfer-",
  ko: "airport-transfer-",
  ckb: "geyandini-firokexane-",
};

// Build a localized city landing URL for the given citySlug + language.
export function cityPath(citySlug, lang) {
  const kw = CITY_KW[lang] || CITY_KW.de;
  return withLang(`/${kw}${citySlug}`, lang);
}

// Given a lang-stripped path and its language, return the citySlug if it is a
// city landing page, else null.
export function extractCitySlug(strippedPath, lang) {
  const seg = (strippedPath || "").replace(/^\//, "");
  const primary = CITY_KW[lang] || CITY_KW.de;
  if (seg.startsWith(primary) && seg.length > primary.length) return seg.slice(primary.length);
  // Accept ANY language's city keyword prefix so marketing links in either
  // spelling resolve (e.g. /en/flughafentransfer-luzern -> the city page).
  for (const kw of Object.values(CITY_KW)) {
    if (kw && seg.startsWith(kw) && seg.length > kw.length) return seg.slice(kw.length);
  }
  return aliasToCity(seg);
}

// hreflang expects BCP-47 codes; our internal codes already work as-is.
export const hreflangCode = (code) => code;

export function stripLang(pathname) {
  const segs = (pathname || "/").split("/");
  const first = segs[1];
  if (LANG_CODES.includes(first)) {
    const rest = "/" + segs.slice(2).join("/");
    const clean = rest.replace(/\/+$/, "");
    return { lang: first, path: clean === "" ? "/" : clean };
  }
  const clean = (pathname || "/").replace(/\/+$/, "");
  return { lang: DEFAULT_LANG, path: clean === "" ? "/" : clean };
}

export function getLangFromPath(pathname) {
  return stripLang(pathname).lang;
}

export function withLang(path, lang) {
  const clean = (path || "/").startsWith("/") ? path : "/" + (path || "");
  if (!lang || lang === DEFAULT_LANG) return clean === "" ? "/" : clean;
  return `/${lang}${clean === "/" ? "" : clean}`;
}

// Localize an internal `to`/`href` string to the given language.
export function localize(to, lang) {
  if (typeof to !== "string") return to;
  if (/^(https?:|mailto:|tel:|#|\/\/)/.test(to)) return to;
  const [p, hash] = to.split("#");
  const bare = stripLang(p || "/").path;
  const prefixed = withLang(bare, lang);
  return hash !== undefined ? `${prefixed}#${hash}` : prefixed;
}

// Hook returning a function that localizes any internal path to the current lang.
export function useLocalizedPath() {
  const { pathname } = useLocation();
  const lang = getLangFromPath(pathname);
  return (to) => localize(to, lang);
}
